import { useAsyncModal } from '@ipfabric/frontend-shared-assets/utils/react/useAsyncModal';
import { isNonNullable } from '@ipfabric/utils';
import { ApiTable } from 'components/ApiTable/components/ApiTable';
import type { ApiTableDataPatch } from 'components/ApiTable/model/ApiTableModel';
import { Button } from 'components/Button/Button';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type {
  TableBulkAction,
  TableBulkActionClickEvent,
} from 'components/Table/model/TableModel';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { LeaveDialog } from 'containers/LeaveDialogue/LeaveDialog';
import { produce } from 'immer';
import { AddToSiteDialog } from 'pages/Inventory/Sites/ManualSeparation/AddToSiteDialog';
import { useSeparateSites } from 'pages/Inventory/Sites/ManualSeparation/useSeparateSites';
import { memo, useMemo, useState } from 'react';
import type { FC } from 'react';
import { Helmet } from 'react-helmet';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';

const APPLY_CONFIRM_DIALOG = 'manual-site-separation-apply-confirm-dialog';

const BareManualSeparationPage: FC = () => {
  const manualSeparationMsg = useLocale().manualSeparation;
  const [newSiteNames, setNewSiteNames] = useState<
    ApiTableDataPatch<{ newSiteName: string }>
  >(() => ({}));
  const addToSiteModal = useAsyncModal<string | null>();
  const separateSites = useSeparateSites();
  const applySeparation = async (global: boolean) => {
    await separateSites(newSiteNames, global);
    setNewSiteNames({});
  };

  const endpoint = useTableEndpoint({
    apiEndpoint: '/tables/inventory/devices',
    alternative: {
      id: 'manual-site-separation',
      title: manualSeparationMsg.pageTitle,
      columns: [
        'hostname',
        'siteName',
        createFrontendOnlyColumn({
          key: 'newSiteName',
          disabledSort: true,
          title: manualSeparationMsg.newSiteNameColumnTitle,
          render: (row) => (
            <span className='font-semibold text-orange-500'>
              {row.newSiteName}
            </span>
          ),
        }),
        'sn',
        'loginIpv4',
        'loginIpv6',
        'loginType',
        'vendor',
        'family',
        'version',
      ],
    },
  });

  const pendingChangesExist = Object.keys(newSiteNames).length > 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies(addToSiteModal.show): considered stable
  const bulkActions = useMemo(
    (): TableBulkAction[] =>
      [
        {
          key: 'add',
          title: 'Change site',
          disableAutoDeselect: true,
          onClick: async ({
            selectedRows,
            deselectAll,
          }: TableBulkActionClickEvent) => {
            const newSiteName = await addToSiteModal.show();
            if (newSiteName !== null) {
              setNewSiteNames(
                produce((draft) => {
                  for (const sn of selectedRows) {
                    draft[sn] = { newSiteName };
                  }
                }),
              );
              deselectAll();
            }
          },
        },
        pendingChangesExist
          ? {
              key: 'reset',
              title: 'Reset change',
              onClick: ({ selectedRows }: TableBulkActionClickEvent) => {
                setNewSiteNames(
                  produce((draft) => {
                    for (const sn of selectedRows) {
                      delete draft[sn];
                    }
                  }),
                );
              },
            }
          : null,
      ].filter(isNonNullable),
    [pendingChangesExist],
  );

  const applyConfirmDialog = useConfirmDialog({ name: APPLY_CONFIRM_DIALOG });

  const showApplyConfirm = () => {
    applyConfirmDialog.toggleConfirmDialog();
  };

  return (
    <>
      <Helmet title={manualSeparationMsg.pageTitle} />
      {addToSiteModal.render((returnFromModal) => (
        <AddToSiteDialog
          onClose={() => returnFromModal(null)}
          onConfirm={returnFromModal}
        />
      ))}
      <ConfirmDialog
        id={APPLY_CONFIRM_DIALOG}
        onClickConfirm={() => {
          applySeparation(false);
        }}
        onClickSecondaryConfirm={() => {
          applySeparation(true);
        }}
        msg={manualSeparationMsg.confirmDialog}
        btnConfirmVariant='primary'
        btnCancelVariant='secondary'
      />
      <LeaveDialog
        isPristine={!pendingChangesExist}
        onSave={showApplyConfirm}
        msg={manualSeparationMsg.leaveDialog}
        isValid
      />
      <div className='mb'>
        <Button
          variant='primary'
          type='button'
          disabled={!pendingChangesExist}
          onClick={showApplyConfirm}
        >
          {manualSeparationMsg.btnApply}
        </Button>
      </div>
      <ApiTable
        bulkActions={bulkActions}
        className='flex-1'
        dataPatch={newSiteNames}
        endpoint={endpoint}
        rowIdField='sn'
      />
    </>
  );
};

export const ManualSeparationPage = memo(BareManualSeparationPage);
